import React, {
    useContext, useCallback, useEffect, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { ModalNextProvider, Loader } from '@jutro/components';
import { TranslatorContext, IntlContext } from '@jutro/locale';
import { MetadataForm } from '@jutro/uiconfig';

import { withAuthenticationContext } from 'gw-digital-auth-react';
import { getNormalizedLOBName } from 'gw-portals-config-js';
import { useDependencies } from 'gw-portals-dependency-react';
import { PaymentComponent } from 'gw-components-platform-react';
import vmTranslator, { messages as commonMessages } from 'gw-platform-translations';

import { ViewModelServiceFactory } from 'gw-portals-viewmodel-js';
import metadata from './BillingSummary.metadata.json5';
import styles from './BillingSummary.module.scss';
import messages from './BillingSummary.messages';
import AutomaticPayments from '../AutomaticPayments/AutomaticPayments';


const policyLevelHeaderStrings = {
    pa: {
        autoPaymentEnabled: messages.personalAutoPolicyAutomaticPaymentsEnabled,
        default: messages.personalAutoPolicy
    },
    ho: {
        autoPaymentEnabled: messages.homeownersPolicyAutomaticPaymentsEnabled,
        default: messages.homeownersPolicy
    },
    bop: {
        autoPaymentEnabled: messages.businessOwnersPolicyAutomaticPaymentsEnabled,
        default: messages.businessOwnersPolicy
    },
    ca: {
        autoPaymentEnabled: messages.commercialAutoPolicyAutomaticPaymentsEnabled,
        default: messages.commercialAutoPolicy
    },
    cp: {
        autoPaymentEnabled: messages.commercialPropertyPolicyAutomaticPaymentsEnabled,
        default: messages.commercialPropertyPolicy
    },
    cpkg: {
        autoPaymentEnabled: messages.commercialPackagePolicyAutomaticPaymentsEnabled,
        default: messages.commercialPackagePolicy
    },
    gl: {
        autoPaymentEnabled: messages.generalLiabilityPolicyAutomaticPaymentsEnabled,
        default: messages.generalLiabilityPolicy
    },
    im: {
        autoPaymentEnabled: messages.inlandMarinePolicyAutomaticPaymentsEnabled,
        default: messages.inlandMarinePolicy
    },
    wc: {
        autoPaymentEnabled: messages.workersCompPolicyAutomaticPaymentsEnabled,
        default: messages.workersCompPolicy
    },
    wc7: {
        autoPaymentEnabled: messages.workersComp7PolicyAutomaticPaymentsEnabled,
        default: messages.workersComp7Policy
    },
    default: {
        autoPaymentEnabled: messages.policyAutomaticPaymentsEnabled,
        default: messages.policy
    }
};

const getAnyPaidInvoices = (invoiceSummary) => {
    return _.filter(invoiceSummary, (item) => {
        const { amount } = item.totalPaid;
        return item.paidStatus === 'fullypaid' || amount !== 0;
    });
};

const getUnpaidInvoices = (invoiceSummary) => {
    return _.filter(invoiceSummary, (item) => item.paidStatus !== 'fullypaid');
};

const hasUnpaidInvoices = (invoiceSummary) => {
    const unpaidInvoices = getUnpaidInvoices(invoiceSummary);
    return unpaidInvoices.length > 0;
};

const createPaymentInstrumentObject = (paymentDetailsVm, paymentMethod) => {
    const paymentDetailsValue = paymentDetailsVm.value;

    const bankAccountData = {
        ...paymentDetailsValue
    };

    const creditCardData = {
        ...paymentDetailsValue
    };

    let detailsValue;
    if (paymentMethod === 'wire') {
        detailsValue = { bankAccountData };
    } else {
        detailsValue = { creditCardData };
    }

    const paymentInstrumentValue = {
        paymentMethod,
        ...detailsValue
    };

    return paymentInstrumentValue;
};

const getPolicyLevelInvoiceStreamHeader = (lobCode, isAutoPaymentEnabled) => {
    const normalizedLOBName = getNormalizedLOBName(lobCode);
    const lobHeader = _.has(policyLevelHeaderStrings, normalizedLOBName)
        ? policyLevelHeaderStrings[normalizedLOBName]
        : policyLevelHeaderStrings.default;

    if (isAutoPaymentEnabled) {
        return lobHeader.autoPaymentEnabled;
    }

    return lobHeader.default;
};

const getInvoiceStreamHeader = (isAccLevelStream, paymentGroupDetails, lobCode) => {
    let header = '';
    const paymentInstrumentSummary = paymentGroupDetails
        ? paymentGroupDetails.paymentInstrumentSummary
        : null;
    const isAutomaticPaymentEnabled = paymentInstrumentSummary != null;
    if (isAccLevelStream) {
        header = isAutomaticPaymentEnabled
            ? messages.invoicesAutomaticPaymentsEnabled
            : messages.invoices;
    } else {
        header = getPolicyLevelInvoiceStreamHeader(lobCode, isAutomaticPaymentEnabled);
    }
    return header;
};

function BillingSummary(props) {
    const {
        invoiceStreamId,
        accountLevelBilling,
        lobCode,
        paymentGroupDetails,
        currency,
        policySummaries,
        authHeader,
        reloadInvoiceDetails,
        isBroker,
        policyNumber
    } = props;

    const intl = useContext(IntlContext);
    const translator = useContext(TranslatorContext);

    const { AccountBillingDetailsService: BillingService, BillingService: BillingService2 } = useDependencies(
        ['AccountBillingDetailsService', 'BillingService']
    );

    // screen states
    const [isLoadingData, updateIsLoadingData] = useState(false);
    const [selectedInvoicesForPayment, updateSelectedInvoicesForPayment] = useState([]);
    const [selectedInvoiceFilter, updateSelectedInvoiceFilter] = useState('planned');
    const [selectedScreen, updateSelectedScreen] = useState('default');
    const [manualAmountToPay, updateManualAmount] = useState(null);
    const [selectedInvoiceAmount, updateSelectedInvoiceAmount] = useState(0);
    const [baseProvince, setBaseProvince] = useState('');

    const payingAmountValue = manualAmountToPay ? manualAmountToPay.amount : 0;
    const {
        invoiceSummary = [],
        relatedPolicies = [],
        paymentInstrument,
        paymentInstrumentSummary,
        periodicity
    } = paymentGroupDetails;


    useEffect(() => {
        if (policySummaries && policySummaries.length > 0) {
            const currentPolicySummaries = policySummaries.filter((summary) =>  summary.periods[0] !== undefined && summary.periods[0].policyId === policyNumber);
            if (currentPolicySummaries !== undefined && currentPolicySummaries.length > 0) {
                setBaseProvince(currentPolicySummaries[0].baseProvince);
            }

        }
    }, [policyNumber]);

    const firstInvoiceDueDate = useMemo(() => {
        const unPaidInvoices = _.filter(invoiceSummary, (invoice) => {
            return invoice.paidStatus !== 'paid';
        });

        const firstUnpaidInvoice = _.minBy(unPaidInvoices, (invoice) => {
            return new Date(invoice.dueDate).getTime();
        });

        return firstUnpaidInvoice ? firstUnpaidInvoice.dueDate : null;
    }, [invoiceSummary]);

    const resetInternalStates = useCallback(() => {
        updateIsLoadingData(true);
        reloadInvoiceDetails(invoiceStreamId, true).finally(() => {
            updateSelectedInvoicesForPayment([]);
            updateSelectedInvoiceFilter('planned');
            updateManualAmount(null);
            updateIsLoadingData(false);
        });
    }, [reloadInvoiceDetails, invoiceStreamId]);

    const onClickConfirmMakePayment = useCallback(
        (paymentDetailsVm, paymentMethod) => {
            const paymentInstrumentValue = createPaymentInstrumentObject(
                paymentDetailsVm,
                paymentMethod
            );

            BillingService.makeDirectBillPayment(
                selectedInvoicesForPayment,
                manualAmountToPay.amount,
                paymentInstrumentValue,
                authHeader
            )
                .then(() => {
                    updateSelectedScreen('paymentConfirmation');
                })
                .catch(() => {
                    ModalNextProvider.showAlert({
                        title: messages.paymentRequestFailed,
                        message: messages.sorryYourPaymentCouldNotBeProcessedAtThisTime,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).then(() => {
                        resetInternalStates();
                    }, _.noop);
                });
        },
        [BillingService, selectedInvoicesForPayment, manualAmountToPay,
            authHeader, resetInternalStates]
    );

    const onClickCancelMakePayment = useCallback(() => {
        updateSelectedScreen();
    }, []);

    const onClickConfirmSetupAutomaticPayment = useCallback(
        (paymentDetailsVm, paymentMethod) => {
            const paymentDetailsValue = paymentDetailsVm.value;

            const bankAccountData = {
                ...paymentDetailsValue
            };

            const creditCardData = {
                ...paymentDetailsValue
            };

            let detailsValue;
            if (paymentMethod === 'wire') {
                detailsValue = { bankAccountData };
            } else {
                detailsValue = { creditCardData };
            }

            const paymentInstrumentValue = {
                paymentMethod,
                ...detailsValue
            };

            BillingService.setPaymentGroupPaymentInformation(
                invoiceStreamId,
                paymentInstrumentValue,
                authHeader
            )
                .then(() => {
                    updateSelectedScreen('automaticConfirmation');
                })
                .catch(() => {
                    ModalNextProvider.showAlert({
                        title: messages.accountUpdateFailed,
                        message: messages.sorryWeWereNotAbleToSetupAutomaticPaymentsOnYourAccount,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).then(() => {
                        resetInternalStates();
                    }, _.noop);
                });
        },
        [BillingService, invoiceStreamId, authHeader, resetInternalStates]
    );

    const onClickCancelSetupAutomaticPayment = useCallback(() => {
        updateSelectedScreen();
    }, []);

    const onClickConfirmChangePaymentSource = useCallback(
        (paymentDetailsVm, paymentMethod) => {
            const paymentInstrumentValue = createPaymentInstrumentObject(
                paymentDetailsVm,
                paymentMethod
            );

            BillingService.setPaymentGroupPaymentInformation(
                invoiceStreamId,
                paymentInstrumentValue,
                authHeader
            )
                .then(() => {
                    updateSelectedScreen('paymentSourceConfirmation');
                })
                .catch(() => {
                    ModalNextProvider.showAlert({
                        title: messages.changingPaymentMethod,
                        message: messages.thereWasAProblemChangingThePaymentMethod,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).then(() => {
                        resetInternalStates();
                    }, _.noop);
                });
        },
        [BillingService, invoiceStreamId, authHeader, resetInternalStates]
    );

    const onClickCancelChangePaymentSource = useCallback(() => {
        updateSelectedScreen();
    }, []);

    const onUpdateSelectedInvoices = useCallback(
        (selectedInvoiceSummariesId) => {
            const selectedInvoiceObjects = _.filter(invoiceSummary, (anInvoiceSummary) => {
                return selectedInvoiceSummariesId.includes(anInvoiceSummary.invoiceId);
            });

            const selectedTotalAmount = _.sumBy(selectedInvoiceObjects, (invoice) => {
                return Number(_.get(invoice, 'amountDue.amount'));
            }).toFixed(2);

            updateSelectedInvoiceAmount(selectedTotalAmount);
            const amountObject = {
                amount: selectedTotalAmount,
                currency: currency
            };

            updateManualAmount(amountObject);
            updateSelectedInvoicesForPayment(selectedInvoiceSummariesId);
        },
        [invoiceSummary, currency]
    );

    const onClickMenuLink = useCallback(
        (evt) => {
            const { id: menuLinkId } = evt.currentTarget;
            let screen;
            switch (menuLinkId) {
                case 'makeAPaymentLinkId':
                    screen = 'payment';
                    break;
                case 'setupAutomaticPaymentsLinkId':
                    screen = 'automatic';
                    break;
                case 'changePaymentMethodLinkId':
                    screen = 'payment-source';
                    break;
                case 'viewPoliciesLinkId':
                    screen = 'policy-list';
                    break;
                case 'viewInvoicesLinkId':
                case 'default':
                    screen = 'default';
                    break;
                default:
                    screen = 'default';
                    resetInternalStates();
                    break;
            }
            updateSelectedScreen(screen);
        },
        [resetInternalStates]
    );

    const onClickFilterChange = useCallback((value) => {
        updateSelectedInvoiceFilter(value);
    }, []);

    const isAmountToPayValid = useCallback((amount) => {
        return selectedInvoiceAmount < amount || amount === 0 || _.isNull(amount);
    }, [selectedInvoiceAmount]);

    useEffect(() => {
        const defaultFilter = hasUnpaidInvoices(invoiceSummary) ? 'planned' : 'paid';
        updateSelectedInvoiceFilter(defaultFilter);
    }, [invoiceSummary]);

    /*if (isLoadingData) {
        return <Loader loaded={!isLoadingData} />;
    }*/

    const overrideProps = {
        '@field': {
            labelPosition: 'left'
        },
        menuLinkContainerId: {
            visible: (invoiceStreamId || accountLevelBilling) && !(selectedScreen === 'automaticConfirmation')
        },
        makeAPaymentLinkId: {
            visible: !paymentInstrumentSummary && hasUnpaidInvoices(invoiceSummary)
        },
        viewPoliciesLinkId: {
            visible: accountLevelBilling
        },
        changePaymentMethodLinkId: {
            visible: !!paymentInstrumentSummary
        },
        setupAutomaticPaymentsLinkId: {
            visible: !paymentInstrumentSummary
        },
        billingInvoicesContainerId: {
            visible: selectedScreen === 'default' && !!invoiceStreamId
        },
        billingInvoicesHeaderId: {
            content: getInvoiceStreamHeader(accountLevelBilling, paymentGroupDetails, lobCode),
            visible: invoiceStreamId || accountLevelBilling
        },
        invoicesFilterToggleId: {
            onValueChange: onClickFilterChange
        },
        unPaidCovCard: {
            header: translator(messages.invoicesToPay)
        },
        paidCovCard: {
            header: translator(messages.paidInvoices)
        },
        billingUnPaidInvoicesComponentId: {
            invoiceSummary: getUnpaidInvoices(invoiceSummary),
            showSelect: true
            /*  selectedInvoiceFilter === 'planned'
                    ? getUnpaidInvoices(invoiceSummary)
                    : getAnyPaidInvoices(invoiceSummary), */
            // showPaidDate: selectedInvoiceFilter === 'paid'
        },
        billingPaidInvoicesComponentId: {
            invoiceSummary: getAnyPaidInvoices(invoiceSummary),
            showPaidDate: false,
            paidInvoices: true
        },
        makePaymentContainerId: {
            visible: selectedScreen === 'payment'
        },
        makePaymentInvoicesTableComponentId: {
            invoiceSummary: getUnpaidInvoices(invoiceSummary),
            selectedInvoices: selectedInvoicesForPayment,
            onUpdateSelectedInvoices: onUpdateSelectedInvoices
        },
        makePaymentSourceComponentId: {
            title: messages.setPaymentSource,
            xCenter: 'bc',
            model: paymentInstrument,
            isDisabled: selectedInvoicesForPayment.length === 0,
            isSelectedInvoiceAmount: manualAmountToPay
                && isAmountToPayValid(manualAmountToPay.amount)
        },
        makePaymentAmountToPayId: {
            defaultCurrency: currency,
            disabled: selectedInvoicesForPayment.length === 0,
            onValueChange: updateManualAmount
        },
        makePaymentConfirmationContainerId: {
            visible: selectedScreen === 'paymentConfirmation'
        },
        makePaymentConfirmationMessageId: {
            content: translator(messages.paymentOfHasBeenAppliedToYourAccount, {
                paidAmount: intl.formatNumber(
                    payingAmountValue,
                    {
                        style: 'currency',
                        currency: currency,
                        currencyDisplay: 'code'
                    }
                )
            })
        },
        setUpAutomaticPaymentContainerId: {
            visible: selectedScreen === 'automatic'
        },
        setupAutomaticPaymentComponentId: {
            xCenter: 'bc',
            model: paymentInstrument
        },
        setUpAutomaticPaymentConfirmationContainerId: {
            visible: selectedScreen === 'automaticConfirmation'
        },
        setUpAutomaticPaymentConfirmationInfoId: {
            content: translator(messages.yourAutomaticPaymentsWillBeginOn, {
                startingDate: intl.formatDate(new Date(firstInvoiceDueDate), { year: 'numeric', month: 'short', day: 'numeric' })
            })
        },
        changePaymentSourceContainerId: {
            visible: selectedScreen === 'payment-source'
        },
        changePaymentSourceFormComponentId: {
            title: messages.changePaymentMethod,
            xCenter: 'bc',
            model: paymentInstrument
        },
        changePaymentSourceConfirmationContainerId: {
            visible: selectedScreen === 'paymentSourceConfirmation'
        },
        policyListContainerId: {
            visible: selectedScreen === 'policy-list'
        },
        viewPoliciesComponentId: {
            policySummaries: policySummaries,
            policyNumbers: relatedPolicies
        },
        billingAccordion: {
            visible: !isBroker
        },
        brokerPaymentInfo: {
            visible: isBroker
        },
        autoPayments: {
            existingPaymentGroupInfo: paymentGroupDetails,
            baseProvnce: baseProvince
        }
    };

    const dataForComponent = {
        invoiceStreamId,
        selectedInvoiceFilter,
        periodicity,
        startingDate: firstInvoiceDueDate,
        manualAmountToPay
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            paymentpagecomponent: PaymentComponent,
            automaticPayments: AutomaticPayments,
        },
        resolveCallbackMap: {
            onClickMenuLink,
            onClickConfirmMakePayment,
            onClickCancelMakePayment,
            onClickConfirmSetupAutomaticPayment,
            onClickCancelSetupAutomaticPayment,
            onClickConfirmChangePaymentSource,
            onClickCancelChangePaymentSource
        }
    };

    return (
        <MetadataForm
            data={dataForComponent}
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

BillingSummary.propTypes = {
    invoiceStreamId: PropTypes.string,
    accountLevelBilling: PropTypes.bool,
    lobCode: PropTypes.string,
    paymentGroupDetails: PropTypes.shape({
        invoiceSummary: PropTypes.array,
        relatedPolicies: PropTypes.array,
        paymentInstrument: PropTypes.object,
        paymentInstrumentSummary: PropTypes.object,
        periodicity: PropTypes.string
    }),
    currency: PropTypes.string.isRequired,
    policySummaries: PropTypes.arrayOf(PropTypes.object),
    authHeader: PropTypes.shape({}).isRequired,
    reloadInvoiceDetails: PropTypes.func.isRequired,
    policyNumber: PropTypes.string,
};

BillingSummary.defaultProps = {
    invoiceStreamId: null,
    accountLevelBilling: null,
    lobCode: null,
    paymentGroupDetails: {
        invoiceSummary: [],
        relatedPolicies: [],
        paymentInstrument: null,
        paymentInstrumentSummary: null,
        periodicity: null,
        policyNumber: null
    },
    policySummaries: []
};

export default withAuthenticationContext(BillingSummary);
